import { useState } from 'react';
import styled from 'styled-components';

import Alert from '../../components/Alert';
import CardPanel from '../../components/CardPanel';
import Link from '../../components/Link';
import {
  IconExport as ExportIcon,
  IconLight as LightIcon,
  IconSliders as SlidersIcon,
} from '../../core';
import bubbles from '../../images/bubles.png';
import dotsChart from '../../images/dots-chart.png';
import horizontalChart from '../../images/horizontal-chart.png';
import { ReactComponent as ContentAreaIcon } from '../../images/icons/content-area.svg';
import customizationBgMobile from '../../images/mobile/customization-bg.jpg';
import dataBgMobile from '../../images/mobile/data-bg.jpg';
import exportShareBgMobile from '../../images/mobile/export-share-bg.jpg';
import featuresBgMobile from '../../images/mobile/features-bg.jpg';
import verticalLinesChart from '../../images/vertical-lines-chart.png';

import CustomizationTab from './components/CustomizationTab';
import DataTab from './components/DataTab';
import ExportShareTab from './components/Export&ShareTab';
import FeaturesTab from './components/FeaturesTab';
import Tabs from './components/Tabs';

interface Props {
  className?: string;
}

const setActiveMobileTopBg = (activeTab: string) => {
  switch (activeTab) {
    case 'customization':
      return customizationBgMobile;
    case 'data':
      return dataBgMobile;
    case 'export':
      return exportShareBgMobile;
    default:
      return featuresBgMobile;
  }
};

const Builder = ({ className = '' }: Props) => {
  const [activeTab, setActiveTab] = useState('features');

  const TABS = [
    {
      id: 'features',
      title: (
        <>
          <LightIcon /> Features
        </>
      ),
      content: <FeaturesTab />,
    },
    {
      id: 'customization',
      title: (
        <>
          <SlidersIcon /> Customization
        </>
      ),
      content: <CustomizationTab />,
    },
    {
      id: 'data',
      title: (
        <>
          <ContentAreaIcon /> Data
        </>
      ),
      content: <DataTab />,
    },
    {
      id: 'export',
      title: (
        <>
          <ExportIcon /> Export & Share
        </>
      ),
      content: <ExportShareTab />,
    },
  ];

  return (
    <div className={className}>
      <section className="hero">
        <Alert
          className="alert"
          iconName="gear"
          mainText="Builder Alert:"
          additionalText="New chart types added to the builder."
          actions={<Link href="/data-visualization">Learn More</Link>}
        />
        <div
          style={{
            backgroundImage: `url(${setActiveMobileTopBg(activeTab)})`,
            height: '350px',
          }}
          className="mobile-bg"
        />

        <Tabs
          defaultActiveTab={activeTab}
          onChange={(tab) => setActiveTab(tab)}
          tabs={TABS}
          className="tabs-container"
        />
      </section>

      <section className="cards">
        <div className="cards-header">
          <h2>Project Gallery</h2>
          <Link className="see-examples-desktop" href="/design-system/sample-dashboards">
            See All Examples
          </Link>
        </div>
        <div className="cards-container">
          <CardPanel imgPath={bubbles} />
          <CardPanel imgPath={dotsChart} />
          <CardPanel imgPath={verticalLinesChart} />
          <CardPanel imgPath={horizontalChart} />
        </div>
        <Link className="see-examples-mobile" href="/design-system/sample-dashboards">
          See All Examples
        </Link>
      </section>
    </div>
  );
};

const StyledBuilder = styled(Builder)`
  margin: 0 auto;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .hero {
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    > .alert {
      margin-top: 16px;
    }

    > .mobile-bg {
      width: 100%;
      display: block;
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center;

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        display: none;
      }
    }

    > .tabs-container {
      padding: 0 16px 72px 16px;

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-left: 48px;
        padding-right: 0;
        padding-bottom: 0;
        margin-top: 20px;
        flex: 1;
        display: flex;
        flex-direction: column;
      }
    }
  }

  > .cards {
    padding: 24px 16px 40px;
    background: linear-gradient(
        to top,
        ${({ theme }) => theme.neutralBackgroundBase},
        ${({ theme }) => theme.neutralBackgroundTransparent}
      ),
      ${({ theme }) => theme.neutralBackgroundMedium};
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 16px 48px;
      border-top: 1px solid ${({ theme }) => theme.neutralBorder};
    }

    > .cards-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      > .see-examples-desktop {
        display: none;
        @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
          display: flex;
          align-items: center;
        }
      }
      > h2 {
        font-size: ${({ theme }) => theme.fontSizes.xl};
        font-weight: 600;
        line-height: ${({ theme }) => theme.lineHeights.xl};
        background: linear-gradient(
          to left,
          /* tokens-allow: pending design decision */ #155676,
          ${({ theme }) => theme.primaryBackgroundStrong}
        );
        color: transparent;
        background-clip: text;
        margin-bottom: 8px;
      }
    }
    > .cards-container {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }

    > .see-examples-mobile {
      margin-top: 28px;
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        display: none;
      }
    }
  }
`;

export default StyledBuilder;
